import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import '../simplified-finance.css';

type Frequency = 'mensual' | 'bimestral' | 'trimestral' | 'anual' | 'variable';
type AmountMode = 'fixed' | 'estimated' | 'variable';

type Service = {
  id: string;
  name: string;
  frequency: string;
  amount_mode: AmountMode;
  default_amount: number | null;
  due_day: number | null;
  is_active: boolean;
};

type ServicePayment = {
  id: string;
  service_id: string;
  expected_amount: number | null;
  actual_amount: number | null;
  due_on: string | null;
  paid_on: string | null;
  payer_type: string | null;
  status: string;
};

type ServiceForm = {
  name: string;
  frequency: Frequency;
  amountMode: AmountMode;
  defaultAmount: string;
  dueDay: string;
};

const emptyServiceForm: ServiceForm = {
  name: '',
  frequency: 'mensual',
  amountMode: 'estimated',
  defaultAmount: '',
  dueDay: '',
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function monthBounds(month: string) {
  const [year, monthNumber] = month.split('-').map(Number);
  return {
    start: `${month}-01`,
    end: new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10),
  };
}

function safeDueDate(month: string, day: number | null) {
  if (!day) return `${month}-01`;
  const { end } = monthBounds(month);
  const maxDay = Number(end.slice(-2));
  return `${month}-${String(Math.min(day, maxDay)).padStart(2, '0')}`;
}

function frequencyLabel(value: string) {
  const labels: Record<string, string> = {
    mensual: 'Mensual',
    bimestral: 'Bimestral',
    trimestral: 'Trimestral',
    anual: 'Anual',
    variable: 'Variable',
  };
  return labels[value] ?? value;
}

export default function SharedFixedExpenses() {
  const [month, setMonth] = useState(currentMonth());
  const [services, setServices] = useState<Service[]>([]);
  const [payments, setPayments] = useState<ServicePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [serviceForm, setServiceForm] = useState<ServiceForm>(emptyServiceForm);
  const [paymentServiceId, setPaymentServiceId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'expected' | 'paid'>('expected');
  const [payerType, setPayerType] = useState('joint_account');

  async function loadData(clearMessage = false) {
    setLoading(true);
    if (clearMessage) setMessage('');
    const { start, end } = monthBounds(month);
    const [serviceResult, paymentResult] = await Promise.all([
      supabase
        .from('shared_services')
        .select('id, name, frequency, amount_mode, default_amount, due_day, is_active')
        .order('is_active', { ascending: false })
        .order('name'),
      supabase
        .from('shared_service_payments')
        .select('id, service_id, expected_amount, actual_amount, due_on, paid_on, payer_type, status')
        .neq('status', 'cancelled')
        .gte('period_month', start)
        .lte('period_month', end),
    ]);

    if (serviceResult.error || paymentResult.error) {
      setMessage('No fue posible cargar los gastos fijos.');
      setLoading(false);
      return;
    }

    const loadedServices = (serviceResult.data ?? []) as Service[];
    setServices(loadedServices);
    setPayments((paymentResult.data ?? []) as ServicePayment[]);
    if (!paymentServiceId && loadedServices.length) {
      const first = loadedServices.find((item) => item.is_active) ?? loadedServices[0];
      setPaymentServiceId(first.id);
      setPaymentAmount(String(first.default_amount ?? ''));
      setPaymentDate(safeDueDate(month, first.due_day));
    }
    setLoading(false);
  }

  useEffect(() => {
    void loadData(true);
  }, [month]);

  const paymentMap = useMemo(
    () => new Map(payments.map((payment) => [payment.service_id, payment])),
    [payments],
  );

  const summary = useMemo(() => {
    let expected = 0;
    let paid = 0;
    for (const service of services.filter((item) => item.is_active)) {
      const payment = paymentMap.get(service.id);
      expected += Number(payment?.expected_amount ?? payment?.actual_amount ?? service.default_amount ?? 0);
      if (payment?.status === 'paid') paid += Number(payment.actual_amount ?? payment.expected_amount ?? 0);
    }
    return { expected, paid, pending: Math.max(0, expected - paid) };
  }, [paymentMap, services]);

  function openNewService() {
    setEditingServiceId(null);
    setServiceForm(emptyServiceForm);
    setShowServiceForm(true);
  }

  function openEditService(service: Service) {
    setEditingServiceId(service.id);
    setServiceForm({
      name: service.name,
      frequency: (service.frequency as Frequency) || 'variable',
      amountMode: service.amount_mode,
      defaultAmount: service.default_amount == null ? '' : String(service.default_amount),
      dueDay: service.due_day == null ? '' : String(service.due_day),
    });
    setShowServiceForm(true);
  }

  async function saveService(event: FormEvent) {
    event.preventDefault();
    const amount = serviceForm.defaultAmount.trim() ? Number(serviceForm.defaultAmount) : null;
    const dueDay = serviceForm.dueDay.trim() ? Number(serviceForm.dueDay) : null;
    if (!serviceForm.name.trim()) {
      setMessage('Escribe el nombre del gasto fijo.');
      return;
    }
    if (amount != null && (!Number.isFinite(amount) || amount < 0)) {
      setMessage('El importe estimado no es válido.');
      return;
    }
    if (dueDay != null && (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31)) {
      setMessage('El día de vencimiento debe estar entre 1 y 31.');
      return;
    }

    setBusy(true);
    const payload = {
      name: serviceForm.name.trim(),
      frequency: serviceForm.frequency,
      amount_mode: serviceForm.amountMode,
      default_amount: amount,
      due_day: dueDay,
      updated_at: new Date().toISOString(),
    };
    const result = editingServiceId
      ? await supabase.from('shared_services').update(payload).eq('id', editingServiceId)
      : await supabase.from('shared_services').insert(payload);

    setMessage(result.error ? 'No fue posible guardar el gasto fijo.' : 'Gasto fijo guardado.');
    if (!result.error) {
      setShowServiceForm(false);
      setEditingServiceId(null);
      setServiceForm(emptyServiceForm);
      await loadData();
    }
    setBusy(false);
  }

  async function toggleService(service: Service) {
    setBusy(true);
    const { error } = await supabase
      .from('shared_services')
      .update({ is_active: !service.is_active, updated_at: new Date().toISOString() })
      .eq('id', service.id);
    setMessage(error ? 'No fue posible cambiar el estado.' : service.is_active ? 'Gasto fijo desactivado.' : 'Gasto fijo reactivado.');
    if (!error) await loadData();
    setBusy(false);
  }

  function choosePaymentService(id: string) {
    setPaymentServiceId(id);
    const service = services.find((item) => item.id === id);
    const existing = paymentMap.get(id);
    setPaymentAmount(String(existing?.actual_amount ?? existing?.expected_amount ?? service?.default_amount ?? ''));
    setPaymentDate(existing?.paid_on ?? existing?.due_on ?? safeDueDate(month, service?.due_day ?? null));
    setPaymentStatus(existing?.status === 'paid' ? 'paid' : 'expected');
    setPayerType(existing?.payer_type ?? 'joint_account');
  }

  async function saveMonthlyPayment(event: FormEvent) {
    event.preventDefault();
    const amount = Number(paymentAmount);
    if (!paymentServiceId || !Number.isFinite(amount) || amount < 0 || !paymentDate) {
      setMessage('Completa el servicio, importe y fecha.');
      return;
    }

    setBusy(true);
    const existing = paymentMap.get(paymentServiceId);
    const payload = {
      service_id: paymentServiceId,
      period_month: `${month}-01`,
      expected_amount: amount,
      actual_amount: paymentStatus === 'paid' ? amount : null,
      due_on: paymentDate,
      paid_on: paymentStatus === 'paid' ? paymentDate : null,
      payer_type: payerType,
      status: paymentStatus,
      notes: 'Registro simplificado de gasto fijo',
      updated_at: new Date().toISOString(),
    };
    const result = existing
      ? await supabase.from('shared_service_payments').update(payload).eq('id', existing.id)
      : await supabase.from('shared_service_payments').insert(payload);

    setMessage(result.error ? 'No fue posible guardar el pago mensual.' : 'Pago mensual actualizado.');
    if (!result.error) await loadData();
    setBusy(false);
  }

  return (
    <section className="simple-page">
      <div className="simple-heading">
        <div>
          <p>Servicios del hogar</p>
          <h1>Gastos fijos</h1>
          <span>Configura agua, luz, predial, internet y cualquier otro gasto compartido sin mezclar tarjetas o cuentas personales.</span>
        </div>
        <div className="simple-heading-actions">
          <label>Periodo<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
          <button type="button" onClick={openNewService}>Agregar gasto</button>
        </div>
      </div>

      {loading && <div className="simple-state">Cargando gastos fijos…</div>}
      {!loading && (
        <>
          <section className="simple-summary simple-summary--three">
            <article><span>Estimado del periodo</span><strong>{currency.format(summary.expected)}</strong></article>
            <article><span>Pagado</span><strong>{currency.format(summary.paid)}</strong></article>
            <article><span>Pendiente</span><strong>{currency.format(summary.pending)}</strong></article>
          </section>

          {showServiceForm && (
            <form className="simple-card simple-form" onSubmit={saveService}>
              <div className="simple-card-heading"><div><small>Configuración</small><h2>{editingServiceId ? 'Modificar gasto fijo' : 'Nuevo gasto fijo'}</h2></div><button type="button" className="simple-link" onClick={() => setShowServiceForm(false)}>Cerrar</button></div>
              <div className="simple-grid">
                <label>Nombre<input value={serviceForm.name} onChange={(event) => setServiceForm({ ...serviceForm, name: event.target.value })} required /></label>
                <label>Frecuencia<select value={serviceForm.frequency} onChange={(event) => setServiceForm({ ...serviceForm, frequency: event.target.value as Frequency })}><option value="mensual">Mensual</option><option value="bimestral">Bimestral</option><option value="trimestral">Trimestral</option><option value="anual">Anual</option><option value="variable">Variable</option></select></label>
                <label>Tipo de importe<select value={serviceForm.amountMode} onChange={(event) => setServiceForm({ ...serviceForm, amountMode: event.target.value as AmountMode })}><option value="fixed">Fijo</option><option value="estimated">Estimado</option><option value="variable">Variable</option></select></label>
                <label>Importe estimado<input type="number" min="0" step="0.01" value={serviceForm.defaultAmount} onChange={(event) => setServiceForm({ ...serviceForm, defaultAmount: event.target.value })} /></label>
                <label>Día habitual<input type="number" min="1" max="31" value={serviceForm.dueDay} onChange={(event) => setServiceForm({ ...serviceForm, dueDay: event.target.value })} /></label>
              </div>
              <button className="simple-primary" type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Guardar gasto fijo'}</button>
            </form>
          )}

          <section className="simple-list">
            {services.map((service) => {
              const payment = paymentMap.get(service.id);
              const amount = Number(payment?.actual_amount ?? payment?.expected_amount ?? service.default_amount ?? 0);
              return (
                <article key={service.id} className={`simple-card simple-service ${service.is_active ? '' : 'is-inactive'}`}>
                  <div>
                    <small>{frequencyLabel(service.frequency)} · {service.amount_mode === 'fixed' ? 'importe fijo' : service.amount_mode === 'estimated' ? 'importe estimado' : 'importe variable'}</small>
                    <h2>{service.name}</h2>
                    <span>{service.due_day ? `Día habitual ${service.due_day}` : 'Sin día definido'}</span>
                  </div>
                  <div className="simple-service-amount"><strong>{currency.format(amount)}</strong><span>{payment?.status === 'paid' ? 'Pagado' : payment ? 'Programado' : 'Sin registro mensual'}</span></div>
                  <div className="simple-actions"><button type="button" onClick={() => openEditService(service)}>Modificar</button><button type="button" onClick={() => void toggleService(service)} disabled={busy}>{service.is_active ? 'Desactivar' : 'Reactivar'}</button></div>
                </article>
              );
            })}
          </section>

          <form className="simple-card simple-form" onSubmit={saveMonthlyPayment}>
            <div className="simple-card-heading"><div><small>Periodo seleccionado</small><h2>Registrar o actualizar pago</h2></div></div>
            <div className="simple-grid">
              <label>Servicio<select value={paymentServiceId} onChange={(event) => choosePaymentService(event.target.value)}>{services.filter((item) => item.is_active).map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></label>
              <label>Importe<input type="number" min="0" step="0.01" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} required /></label>
              <label>Fecha<input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} required /></label>
              <label>Estado<select value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value as 'expected' | 'paid')}><option value="expected">Esperado</option><option value="paid">Pagado</option></select></label>
              <label>Pagado por<select value={payerType} onChange={(event) => setPayerType(event.target.value)}><option value="joint_account">Fondo del proyecto</option><option value="juan_carlos">Juan Carlos</option><option value="ximena">Ximena</option><option value="third_party">Tercero</option></select></label>
            </div>
            <button className="simple-primary" type="submit" disabled={busy || !paymentServiceId}>{busy ? 'Guardando…' : 'Guardar pago mensual'}</button>
          </form>
        </>
      )}

      {message && <p className="simple-message" role="status">{message}</p>}
    </section>
  );
}
